import { RssFeedI, PostingQueueArticleI } from "../../../interfaces/core.interface";
import { SocialAiArticlesFormComponent } from "../../forms/social-ai-articles-form/social-ai-articles-form.component";
import { CoreService } from "../../../services/core.service";
import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, TemplateRef, ViewChild, OnDestroy, AfterViewInit } from "@angular/core";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule, MatSelectChange } from "@angular/material/select";
import { MatTabsModule } from "@angular/material/tabs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { DragDropModule, moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";
import { forkJoin, Subject, merge, of as observableOf, EMPTY } from "rxjs";
import { switchMap, takeUntil, startWith, catchError, filter, delay, tap, finalize } from "rxjs/operators";
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};



type SocialTag =
  | number
  | null
  | "photolink"
  | "fbphotopost"
  | "fbfirstcomment"
  | "fbvideo"
  | "fbexclude"
  | "igphotopost"
  | "igvideo"
  | "igcarousel"
  | "tiktokvideo"
  | "tiktokcarousel";

@Component({
  selector: "app-social-ai-queue-table",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTabsModule,
    MatCheckboxModule,
    DragDropModule,
    MatTooltipModule
  ],
  templateUrl: "./social-ai-queue-table.component.html",
  styleUrl: "./social-ai-queue-table.component.scss",
})
export class SocialAiQueueTableComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';


  selectedPlatformsMap: { [postingQueueId: number]: string } = {};

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      this.selectedFeed = null;
      this.companyIdStream$.next();
    }
  }

  get getCompanyId(): string | null {
    return this._companyID;
  }

  @Input() formData: any;
  @ViewChild("articleFormTmpl", { static: true })
  articleFormTmpl!: TemplateRef<any>;
  @ViewChild("confirmDeleteArticleTmpl", { static: true })
  confirmDeleteArticleTmpl!: TemplateRef<any>;

  selectedArticle: PostingQueueArticleI = {} as PostingQueueArticleI;
  selectedFeed: RssFeedI | null = null;
  private allFeedsForSelectedRss: PostingQueueArticleI[] = [];
  facebookArticles: PostingQueueArticleI[] = [];
  instagramArticles: PostingQueueArticleI[] = [];
  platformArticles: { [platformName: string]: PostingQueueArticleI[] } = {};
  isLoadingArticles: boolean = false;
  showFailedArticlesTable: boolean = false;
  failedArticles: PostingQueueArticleI[] = [];

  selectOptions: {
    feeds: RssFeedI[];
  } = {
      feeds: [],
    };

  tagMap: { [key: string]: string } = {
    photolink: "Link Post",
    fbphotopost: "Photo Post",
    fbfirstcomment: "Photo Post With First Comment",
    fbvideo: "Reel Post",
    igphotopost: "Photo Post",
    igvideo: "Reel Post",
    igcarousel: "Carousel Post",
    fbstoryvideo: "Story Video",
    igstoryvideo: "Story Video",
    fbstoryphoto: "Story Photo",
    igstoryphoto: "Story Photo",
    tiktokcarousel: "Carousel Post",
    tiktokvideo: "Reel Post",
    tiktokphoto: "Photo Post",
    tiktokstoryvideo: "Story Video",
    tiktokstoryphoto: "Story Photo",
    postnow: "Post Now",
  };

  constructor(public coreService: CoreService, public dialog: MatDialog, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  ngAfterViewInit(): void {
    merge(this.companyIdStream$.asObservable())
      .pipe(
        takeUntil(this.destroy$),
        delay(0),
        filter(() => {
          const isCompanyIdPresent =
            this._companyID !== null && this._companyID !== undefined;
          if (!isCompanyIdPresent) {
            console.warn(
              "SocialAiQueueTableComponent: companyID is not yet available, skipping feed fetch."
            );
          }
          return isCompanyIdPresent;
        }),
        startWith({}),
        switchMap(() => this.getFeedsForCompanyObservable()),
        catchError((error) => {
          console.error("Error in data loading merge pipe for feeds:", error);
          this.coreService.showSnackbar("Failed to load Categories.");
          return observableOf({ data: [], total: 0 });
        })
      )
      .subscribe((data: any) => {
        this.selectOptions.feeds = data.data || [];
      });

    if (this._companyID !== null) {
      this.companyIdStream$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFeedsForCompanyObservable() {
    if (!this._companyID) {
      console.warn(
        "getFeedsForCompanyObservable called without companyID. This should not happen."
      );
      return observableOf({ data: [], total: 0 });
    }

    return this.coreService
      .getData(`/getFeedAndSiteDomain/dropdown?companyID=${this._companyID}`)
      .pipe(
        catchError((error) => {
          console.error("Error fetching Categories for company:", error);
          this.coreService.showSnackbar("Error fetching Categories.");
          return observableOf({ data: [], total: 0 });
        })
      );
  }

  drop(event: CdkDragDrop<PostingQueueArticleI[]>, platformName: string) {
    const targetArray = this.platformArticles[platformName];

    if (!targetArray || targetArray.length === 0) {
      return;
    }

    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(targetArray, event.previousIndex, event.currentIndex);

    const fromArticle = targetArray[event.currentIndex];
    const toArticle = targetArray[event.previousIndex];

    const tempPostTime = fromArticle.postTime;
    const tempScheduledFor = fromArticle.scheduled_for;

    fromArticle.postTime = toArticle.postTime;
    fromArticle.scheduled_for = toArticle.scheduled_for;

    toArticle.postTime = tempPostTime;
    toArticle.scheduled_for = tempScheduledFor;

    this.coreService
      .postData(
        `updatePostingQueueFromDragAndDrop/${fromArticle.posting_queue_id}`,
        {
          postTime: fromArticle.postTime,
          scheduled_for: fromArticle.scheduled_for,
        }
      )
      .subscribe({
        error: (err) =>
          console.error(`Error updating article ${fromArticle.id}:`, err),
      });

    this.coreService
      .postData(
        `updatePostingQueueFromDragAndDrop/${toArticle.posting_queue_id}`,
        {
          postTime: toArticle.postTime,
          scheduled_for: toArticle.scheduled_for,
        }
      )
      .subscribe({
        error: (err) =>
          console.error(`Error updating article ${toArticle.id}:`, err),
      });
  }

  getDateLabel(dateStr: string): string {
    const now = new Date();
    const date = new Date(dateStr);

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    if (dateOnly.getTime() === today.getTime()) {
      return `Today, ${date.toLocaleTimeString([], timeOptions)}`;
    }

    if (dateOnly.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${date.toLocaleTimeString([], timeOptions)}`;
    }

    return date.toLocaleDateString("en-EN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  getPostingQueueForFeeds(event: MatSelectChange | RssFeedI): void {
    this.showFailedArticlesTable = false;
    this.failedArticles = [];

    const newSelectedFeed = (event as MatSelectChange).value
      ? (event as MatSelectChange).value
      : (event as RssFeedI);
    this.selectedFeed = newSelectedFeed;

    if (!this.selectedFeed || !this.selectedFeed.id || !this.getCompanyId) {
      this.allFeedsForSelectedRss = [];
      this.platformArticles = {};
      this.isLoadingArticles = false;
      return;
    }

    this.isLoadingArticles = true;

    forkJoin({
      limit: this.coreService.getData(
        `/allowpostspermonth/${this.getCompanyId}`
      ),
      totalPostedPerCompanyData: this.coreService.getData(
        `/getTotalPostedPerCompany/${this.getCompanyId}`
      ),
      posts: this.coreService.getData(
        `/getPostingQueue/${this.getCompanyId}/${this.selectedFeed.id}`
      ),
    }).subscribe({
      next: ({ limit, totalPostedPerCompanyData, posts }) => {
        this.allFeedsForSelectedRss = posts || [];
        this.filterArticlesByPlatform();
        this.isLoadingArticles = false;

        let currentCompanyTotalPosts = 0;
        if (
          Array.isArray(totalPostedPerCompanyData) &&
          totalPostedPerCompanyData.length > 0
        ) {
          currentCompanyTotalPosts = totalPostedPerCompanyData[0].total_posts;
        }

        if (currentCompanyTotalPosts >= limit) {
          this.coreService.showSnackbar(
            "You have reached the posting limit for this month."
          );
        }
      },
      error: (error) => {
        console.error(
          "Error fetching data (posting queue, limit, or total posted):",
          error
        );
        this.coreService.showSnackbar("Error fetching data.");
        this.isLoadingArticles = false;
      },
    });
  }

  private filterArticlesByPlatform(): void {
    this.platformArticles = {};
    this.allFeedsForSelectedRss.forEach((article) => {
      if (String(article.tag) === "fbexclude") {
        return;
      }

      const platform = article.social_platform_name;
      if (!this.platformArticles[platform]) {
        this.platformArticles[platform] = [];
      }
      this.platformArticles[platform].push(article);
    });

    for (const platform in this.platformArticles) {
      this.platformArticles[platform].sort(
        (a, b) =>
          new Date(a.scheduled_for).getTime() -
          new Date(b.scheduled_for).getTime()
      );
    }
  }

  getPlatformIcon(platformName: string): string {
    const platform = platformName.toLowerCase();

    switch (platform) {
      case "facebook":
        return "facebook";
      case "instagram":
        return "camera_alt";
      case "tiktok":
        return "music_note";
      default:
        return "share";
    }
  }

  getPlatformNames(): string[] {
    return Object.keys(this.platformArticles);
  }

  hasNoArticles(): boolean {
    if (this.isLoadingArticles || !this.selectedFeed) {
      return false;
    }

    return (
      this.getPlatformNames().length === 0 ||
      Object.values(this.platformArticles).every((arr) => arr.length === 0)
    );
  }

  getTagName(tagValue: SocialTag | undefined): string {
    if (tagValue === undefined) {
      return "-";
    }
    const key = tagValue === null ? "null" : String(tagValue);
    return this.tagMap[key] || "Unknown";
  }

  editArticle(row: PostingQueueArticleI): void {
    this.selectedArticle = row;
    const dialogRef = this.dialog.open(SocialAiArticlesFormComponent, {
      data: {
        formData: this.selectedArticle,
        isFailedArticle: this.showFailedArticlesTable,
      },
      minWidth: "80vw",
      maxWidth: "700px",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onSubmitClbk();
      }
    });
  }

  deleteArticle(article: PostingQueueArticleI): void {
    this.coreService
      .postData(`deleteArticle/${article.posting_queue_id}`, {})
      .subscribe(() => {
        this.onSubmitClbk();
      });
  }

  checkForFailedArticles(): void {
    if (!this.selectedFeed || !this.selectedFeed.id || !this.getCompanyId) {
      this.coreService.showSnackbar("Please select a Category first.");
      return;
    }
    this.isLoadingArticles = true;

    this.coreService
      .getData(
        `/getFailedPostingQueue/${this.getCompanyId}/${this.selectedFeed.id}`
      )
      .pipe(
        catchError((error) => {
          console.error("Error fetching failed articles:", error);
          this.coreService.showSnackbar("Error fetching failed articles.");
          this.isLoadingArticles = false;
          return observableOf([]);
        })
      )
      .subscribe((data: PostingQueueArticleI[]) => {
        this.failedArticles = data || [];
        this.selectedPlatformsMap = {}; // clear previous state
        this.failedArticles.forEach(article => {
          this.selectedPlatformsMap[article.posting_queue_id] = article.social_platform_name || '';
        });
        this.showFailedArticlesTable = true;
        this.isLoadingArticles = false;
        if (this.failedArticles.length === 0) {
          this.coreService.showSnackbar("No failed articles found for this Category.");
        }
      });
  }

  goBackToPostingQueue(): void {
    this.showFailedArticlesTable = false;
    this.failedArticles = [];
    if (this.selectedFeed) {
      this.getPostingQueueForFeeds(this.selectedFeed);
    }
  }

  onCheckboxChange(article: PostingQueueArticleI, checked: boolean): void {
    const selectedPlatform = this.selectedPlatformsMap[article.posting_queue_id];

    if (!selectedPlatform) {
      this.coreService.showSnackbar("Please select a platform first.");
      return;
    }

    const payload = {
      posting_queue_id: article.posting_queue_id,
      platform: selectedPlatform,
      checked: checked,
    };

    this.coreService
      .postData('updateFailedArticle', payload)
      .pipe(
        tap(() => {
          this.coreService.showSnackbar("Article status updated.");
        }),
        catchError((error) => {
          console.error("Error updating article:", error);
          this.coreService.showSnackbar("Failed to update article.");
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.checkForFailedArticles();
      });
  }



  /**
   * Manually triggers the posting of a specific article.
   * @param article The article to be posted.
   */
  postArticleManually(article: PostingQueueArticleI): void {
    if (!article.posting_queue_id) {
      this.coreService.showSnackbar("Article ID is missing. Cannot post.");
      return;
    }

    this.isLoadingArticles = true;

    this.coreService
      .postData(`publishArticleManually/${article.posting_queue_id}`, {})
      .pipe(
        tap((response: any) => {
          if (response && response.status === "success") {
            this.coreService.showSnackbar(
              response.message || "Article posting successfully!"
            );
          } else if (response && response.status === "fail") {
            this.coreService.showSnackbar(
              response.message || "Article failed to post."
            );
          } else {
            this.coreService.showSnackbar(
              "Received an unexpected response status."
            );
          }
        }),
        catchError((error) => {
          console.error("HTTP Error manually posting article:", error);
          const errorMessage =
            error.error?.message || error.message || "Unknown error occurred.";
          this.coreService.showSnackbar(`Failed to post article!`);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoadingArticles = false;
          this.onSubmitClbk();
        })
      )
      .subscribe();
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();
    if (this.selectedFeed && this.getCompanyId) {
      this.coreService
        .getData(
          `/getPostingQueue/${this.getCompanyId}/${this.selectedFeed.id}`
        )
        .subscribe((data: PostingQueueArticleI[]) => {
          this.allFeedsForSelectedRss = data || [];
          this.filterArticlesByPlatform();
        });
    }
  }

  //Sidebar helpers 
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      if (Array.isArray(parsed)) {
        this.posts = parsed.slice(0, this.maxSidebarCards);
      } else {
        this.posts = [];
      }
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
    }
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;

  trackByAlertId = (_: number, item: Alert) => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }
}
